import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { apm } from '@elastic/apm-rum'
import {
  Button,
  TabPane,
  Tabs,
  useGetGapReport,
  useGetGapReportDocument,
  useSEOCountriesJSON,
} from '@pepper/ui'
import type { GapReportDocumentV3 } from '@pepper/utils/src/types/seo'
import { useModal } from '@ebay/nice-modal-react'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { tabs } from './GapDrawer.utils'

import CreateItemDropdown from 'components/CreateItemDropdown'
import ContentGuidelines from 'container/Projects/LiveProjects/Project/CRR/CRRDrawer/ContentGuidelines/ContentGuidelines'
import Outline from 'container/Projects/LiveProjects/Project/CRR/CRRDrawer/Outline/Outline'
import StatHeader from 'container/Projects/LiveProjects/Project/CRR/CRRDrawer/StatHeader/StatHeader'
import IdeaLabDrawer from 'container/Projects/LiveProjects/Project/IdeaLab/IdeaLabDrawer'
import FAQs from 'container/Projects/LiveProjects/Project/CRR/CRRDrawer/FAQs/FAQs'

import CompetitorLogo from 'container/SEOTools/ContentGapReport/Insights/Card/CompetitorLogo'
import CreateItemModal from 'components/CreateItemModal/CreateItemModal'
import { useShareGapReport } from 'container/SEOTools/hooks/useShareGapReport'
import ShareReport from 'container/SEOTools/ContentGapReport/Metrics/ShareReport/ShareReport'

import styles from './GapDrawer.module.css'

type Props = {
  open: boolean
  reportId: string
  documentId: string
  onClose: () => void
}

const PENDING_STATUS_ID = 1
const PROCESS_STATUS_ID = 2
const ERROR_STATUS_ID = 4
const REPORT_TYPE = 'GAP'

function GapDrawer({
  open,
  onClose,
  reportId,
  documentId,
}: Props): JSX.Element {
  const companyId = useSelectedCompanyId()
  const createProjectModal = useModal(CreateItemModal)
  const [link, setLink] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const { data: countriesJson } = useSEOCountriesJSON()

  const { data: reportData, isLoading: isReportLoading } = useGetGapReport({
    reportId,
    companyId,
  })

  const { data } = useGetGapReportDocument<GapReportDocumentV3>({
    reportId,
    companyId,
    documentId,
  })
  const gapDocumentData = data?.document

  const { mutateAsync: shareReport, isLoading: isCreating } =
    useShareGapReport()

  const handleShare = async () => {
    if (!reportData) return null
    const response = await shareReport({
      companyId,
      documentId: reportData.document_id,
      reportId: reportData.id,
    })
    if (response?.data) {
      setIsOpen(true)
      setLink(response.data?.share_url ?? '')
    }
  }

  const getIsOutlineLoading = () => {
    const isGeneratingReport = reportData
      ? [PENDING_STATUS_ID, PROCESS_STATUS_ID, ERROR_STATUS_ID].includes(
          reportData?.generation_status_id
        )
      : isReportLoading

    return isGeneratingReport
  }

  const getTabElement = (key: string) => {
    const primaryKeyword = gapDocumentData?.primary_keyword
    const secondaryKeywords = gapDocumentData?.secondary_keywords

    switch (key) {
      case 'guidelines':
        return (
          <ContentGuidelines
            reportId={reportData?.id}
            reportType={REPORT_TYPE}
            itemId={reportData?.item_id}
            report={gapDocumentData}
            generationStatusId={reportData?.generation_status_id}
            primaryKeyword={{
              keyword: primaryKeyword?.keyword ?? '',
              heading_presence: primaryKeyword?.heading_presence ?? 0,
              min: primaryKeyword?.min ?? 0,
              max: primaryKeyword?.max ?? 0,
              search_volume: primaryKeyword?.search_volume ?? 0,
            }}
            secondaryKeywords={secondaryKeywords?.map(secondary => ({
              keyword: secondary.keyword,
              max: secondary.min,
              min: secondary.max,
              heading_presence: secondary.heading_presence,
              search_volume: secondary.search_volume,
            }))}
          />
        )
      case 'outline':
        return (
          <Outline
            pageStats={gapDocumentData?.page_stats}
            ai_outline={gapDocumentData?.ai_outline}
            competitors={gapDocumentData?.competitors}
            isGeneratingReport={getIsOutlineLoading()}
          />
        )
      case 'faq':
        return (
          <FAQs
            faqs={gapDocumentData?.faqs ?? []}
            alert={gapDocumentData?.page_stats.questions ? 'success' : 'error'}
            alertText={
              gapDocumentData?.page_stats.questions
                ? 'You have an FAQ schema present on your website. We recommend answering the following questions in your content'
                : 'Add a FAQ schema to your blog page. Answer these questions in your content'
            }
            logoType="icon"
          />
        )
      default:
        return null
    }
  }

  const handleOrderContent = async (type: 'private_doc' | 'assignment') => {
    const DEFAULT_VERTICAL_ID = 20
    createProjectModal.show({
      items: [
        {
          title: reportData?.page_title,
          content_gap_report_id: reportData?.id,
          word_count: Math.floor(gapDocumentData?.page_stats.length ?? 0),
        },
      ],
      type,
      title:
        type === 'assignment'
          ? 'Order from Expert Talent'
          : 'Create Item with PepperDocs',
      defaultValues: { verticalId: DEFAULT_VERTICAL_ID },
    })
  }

  useEffect(() => {
    setLink(reportData?.share_url ?? '')
    if (reportData?.generation_status_id === ERROR_STATUS_ID) {
      apm.captureError('Report generation failed')
      apm.addLabels({
        reportId: reportData.id,
      })
    }
  }, [reportData])

  const keywordStat = gapDocumentData?.target_keyword_stats
  const statsData = {
    target_keyword: keywordStat?.keyword,
    stats: [
      {
        title: 'Your Position',
        value: keywordStat?.position,
      },
      {
        title: 'Volume',
        value: keywordStat?.volume,
      },
      {
        title: 'Difficulty',
        value: keywordStat?.difficulty,
      },
      {
        title: 'Current Org. Traffic',
        value: gapDocumentData?.traffic?.current,
      },
      {
        title: 'Potential Traffic',
        value: gapDocumentData?.traffic?.potential,
      },
    ],
  }

  return (
    <IdeaLabDrawer
      className={styles.drawer}
      title={
        <div className={styles.title}>
          <Icon
            icon="heroicons:sparkles-solid"
            width={18}
            height={18}
            className={styles.icon}
          />
          <div className={styles.header}>
            <div className={styles.drawerContainer}>
              <div className={styles.drawerTitle}>
                <h2>{reportData?.page_title}</h2>
                <a
                  href={reportData?.page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.headerLink}
                >
                  <Icon icon="lucide:external-link" width={16} height={16} />
                </a>
              </div>
              {reportData?.region_code ? (
                <div
                  className={styles.flag}
                  title={countriesJson?.[reportData?.region_code].label}
                >
                  <img
                    alt={reportData?.region_code}
                    className={styles.flagImg}
                    height={16}
                    width={20}
                    src={countriesJson?.[reportData?.region_code].flag}
                  />
                </div>
              ) : null}
            </div>
            <div className={styles.headerBtnContainer}>
              <Button
                onClick={!link ? () => handleShare() : () => setIsOpen(true)}
                loading={isCreating}
                size="middle"
                className={styles.btnShareReport}
              >
                <Icon icon="lucide:share" width={16} height={16} />
              </Button>

              {reportData?.item_id ? (
                <Button
                  type="primary"
                  icon={<Icon icon="uil:file-alt" />}
                  className={styles.btnPrimary}
                  target="_blank"
                  href={
                    reportData?.item_type === 'private_doc'
                      ? `/${companyId}/items/${reportData?.item_id}`
                      : `/${companyId}/document/${reportData?.item_id}`
                  }
                >
                  Open In Docs
                </Button>
              ) : (
                <CreateItemDropdown
                  onCreateOrderClick={() => handleOrderContent('assignment')}
                  onCreatePrivateDocClick={() =>
                    handleOrderContent('private_doc')
                  }
                >
                  <Button type="primary" className={styles.btnPrimary}>
                    Revamp Content
                  </Button>
                </CreateItemDropdown>
              )}
            </div>
            {isOpen && (
              <ShareReport
                onClose={() => setIsOpen(false)}
                link={link}
                className={styles.shareReportPopup}
              />
            )}
          </div>
        </div>
      }
      open={open}
      onClose={onClose}
    >
      {gapDocumentData?.target_keyword_stats ? (
        <StatHeader
          statsData={statsData}
          report={reportData}
          targetKeywordStats={gapDocumentData?.target_keyword_stats}
        />
      ) : null}

      <Tabs
        className={styles.tabs}
        tabBarExtraContent={
          <div className={styles.tabBarExtraContent}>
            <div className={styles.tabBarExtraContentText}>
              <Icon icon="lucide:lightbulb" width={16} height={16} />
              <p>Insights picked from top performing pages</p>
            </div>

            <div>
              {gapDocumentData?.competitors.slice(0, 5).map(competitor => {
                const { url } = competitor
                return (
                  <CompetitorLogo
                    key={url}
                    className={styles.logoStack}
                    competitor={{ url }}
                  />
                )
              })}
            </div>
          </div>
        }
      >
        {tabs.map(tab => (
          <TabPane
            key={tab.key}
            className={styles.tab}
            tab={
              <div className={styles.tabTitle}>
                <Icon icon={tab.icon} width="16" height="16" /> {tab.title}
              </div>
            }
          >
            {getTabElement(tab.key)}
          </TabPane>
        ))}
      </Tabs>
    </IdeaLabDrawer>
  )
}

export default GapDrawer
